import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import defaultImage from 'assets/images/property/littlehouse.png';
import { Property } from 'models/Properties';
import SelectField from '../../selectField/SelectField';
import {
  getPropertyByUid,
  getPropertyOptions,
} from '../../../../utils/property/propertyUtils';
import { PropertySelectFieldProps } from './PropertySelectField.types';
import { PropertyLabelPicture } from './PropertySelectField.styles';

function getPropertyPicture(propertyUid: string, properties: Property[]) {
  if (!propertyUid || !properties) {
    return '\u00a0'; // there must be some whitespace character rendered in order to keep the alignment of the dropdown
  }

  const { mainPicture } = getPropertyByUid(properties, propertyUid) || {};

  return (
    <PropertyLabelPicture
      alt=""
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      src={mainPicture?.largeThumbnail || defaultImage}
    />
  );
}

/**
 * This is meant to be used with useFetchAllProperties, these are the necessary fields for this component to work.
 */
export const propertySelectFieldFetchAllQuery = {
  fields: `
    uid,
    name,
    businessType,
    subUnits{
      uid,
      name,
      businessType,
    },
    unitTypes{
      uid,
      hotelUid
      name,
      businessType,
      bathroomsNumber,
      bedroomsNumber,
      mainPicture{tinyThumbnail},
      availability{maxGuests},
      isActive,
      tags,
      units{
        uid,
        name,
        isActive,
      }
    },
    units{
      uid,
      name,
      isActive,
    }
  `,
  conditions: `isActive:true, limit:"9999", topLevelOnly:true,returnTopLevelUnitsWithMatchingSubLevels:true, sortStrategy:"SORT_BY_NAME"`,
};

const PropertySelectField: FC<PropertySelectFieldProps> = ({
  emptyValueLabel,
  name,
  label,
  pictureAsLabel,
  properties,
  enableUnitTypes = true,
  enableHotels = true,
  showUnits = true,
  showInactiveProperties = true,
  shouldShowUnitDefault,
  enableOnlyUnitsFromUnitTypeUid,
  ...rest
}) => {
  const { watch, setValue } = useFormContext();
  const currentValue = watch(name);
  const unitUid = watch('unitUid');

  const labelToRender = pictureAsLabel
    ? getPropertyPicture(currentValue, properties)
    : label;

  useEffect(() => {
    if (shouldShowUnitDefault) {
      setValue(name, unitUid || currentValue);
    }
  }, []);

  const options = [
    ...(emptyValueLabel
      ? [
          {
            label: emptyValueLabel,
            value: '',
          },
        ]
      : []),
    // @ts-expect-error TS2461 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    ...(properties || []).reduce(
      // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      (acc, property) => [
        ...acc,
        ...getPropertyOptions({
          property,
          enableUnitTypes,
          enableHotels,
          showInactiveProperties,
          showUnits,
          enableOnlyUnitsFromUnitTypeUid,
        }),
      ],
      [],
    ),
  ];

  return (
    <SelectField
      label={labelToRender}
      name={name}
      options={options}
      {...rest}
    />
  );
};

export default PropertySelectField;
